import { CommonModule } from "@angular/common";
import { Component, computed, type OnDestroy, type OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { type MatSlideToggle, MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { RouterLink } from "@angular/router";
import { ngxEditorConfig } from "@app/config/angular-editor.config";
import { silentSessionExpired } from "@interceptors/silent-request.token";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { AppStateService, RequestService } from "@services/index";
import type { IApiResponse } from "../../../models";

interface Segment {
  key: string;
  label: string;
  rule: string;
  objective: string;
  total: number;
  skippedRecent: number;
  eligible: number;
}

/** An automated email: on or off per market, sent to each user once, as they qualify. */
interface Automation {
  key: string;
  label: string;
  rule: string;
  objective: string;
  iso2: string;
  enabled: boolean;
  subject: string;
  bodyHtml: string;
  updatedAt: string | null;
  updatedBy: string | null;
  sent: number;
  sent24h: number;
  checked: number;
  failed: number;
  recent: Array<{
    userId: number;
    name: string;
    email: string;
    status: string;
    cartTotal: number | null;
    deliveryFee: number | null;
    sentAt: string | null;
    updatedAt: string;
    error: string | null;
  }>;
}

interface Campaign {
  id: number;
  segment: string;
  subject: string;
  status: string;
  createdAt: string;
  finishedAt: string | null;
  createdBy: string | null;
  recipientCount: number;
  skippedRecent: number;
  sent: number;
  failed: number;
  pending: number;
  skipped: number;
  /** Of those skipped: unsubscribed, blocked, past the test limit (not production). */
  skippedUnsubscribed?: number;
  skippedBlocked?: number;
  skippedTestLimit?: number;
  /** Unsubscribed after this, their last outreach email. */
  unsubscribed?: number;
}

/**
 * Email Outreach: five segments of active shoppers (not vendors or admins) in
 * one market at a time. For each, the number of users, and a subject and
 * body to send them - one email per user, sent in the background under a
 * daily cap. A test goes to the admin's own address first.
 */
@Component({
  selector: "fh-email-outreach-panel",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    AngularEditorModule,
    RouterLink,
  ],
  templateUrl: "./email-outreach-panel.component.html",
  styleUrl: "./email-outreach-panel.component.scss",
})
export class EmailOutreachPanelComponent implements OnInit, OnDestroy {
  readonly markets = computed(() => this.appState.liveCountries().map((c) => c.iso2));
  market = signal<string>("SG");

  segments = signal<Segment[]>([]);
  /** People unsubscribed from Email Outreach: in the selected market, and in all. */
  unsubscribed = signal<{ market: number; total: number } | null>(null);
  campaigns = signal<Campaign[]>([]);
  automations = signal<Automation[]>([]);
  /** The automated emails as being edited, per market and automation. */
  autoDrafts: Record<string, { subject: string; body: string }> = {};
  loading = signal<boolean>(false);
  busy = signal<string>("");

  /** What the admin is writing, per market and segment, kept while they switch tabs. */
  drafts: Record<string, { subject: string; body: string }> = {};

  readonly editorConfig = {
    ...ngxEditorConfig,
    height: "260px",
    enableToolbar: true,
    showToolbar: true,
    placeholder: "Write the email. {{first_name}} becomes each person's first name.",
    toolbarHiddenButtons: [
      ["subscript", "superscript", "indent", "outdent", "fontName"],
      ["fontSize", "backgroundColor", "customClasses", "insertImage", "insertVideo"],
    ],
  };

  private poll?: ReturnType<typeof setInterval>;

  constructor(
    private requestService: RequestService,
    private appState: AppStateService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.load();
    // While anything is sending, keep its progress current. A background poll: it stops
    // (rather than redirecting to /login) once the session has expired.
    this.poll = setInterval(() => {
      if (silentSessionExpired()) return;
      if (this.campaigns().some((c) => c.status === "queued" || c.status === "sending")) {
        this.loadCampaigns(true);
      }
    }, 15000);
  }

  ngOnDestroy(): void {
    if (this.poll) clearInterval(this.poll);
  }

  draft(key: string): { subject: string; body: string } {
    const id = `${this.market()}:${key}`;
    this.drafts[id] ??= { subject: "", body: "" };
    return this.drafts[id];
  }

  setMarket(iso2: string): void {
    this.market.set(iso2);
    this.load();
  }

  load(): void {
    // The market asked for; a reply for a market no longer selected is dropped.
    const iso2 = this.market();
    this.loading.set(true);
    this.requestService
      .get<IApiResponse>(`/api/v2/email-outreach/segments?iso2=${iso2}`)
      .subscribe({
        next: (res) => {
          if (iso2 !== this.market()) return;
          this.segments.set(res.status ? (res.data?.segments ?? []) : []);
          this.unsubscribed.set(res.status ? (res.data?.unsubscribed ?? null) : null);
          this.loading.set(false);
        },
        error: () => {
          if (iso2 !== this.market()) return;
          this.loading.set(false);
          this.snackBar.open("Could not count the segments", "Close", { duration: 4000 });
        },
      });
    this.loadCampaigns();
    this.loadAutomations();
  }

  loadAutomations(): void {
    const iso2 = this.market();
    this.requestService
      .get<IApiResponse>(`/api/v2/email-outreach/automations?iso2=${iso2}`)
      .subscribe((res) => {
        // Another market was picked meanwhile: these are not its automations.
        if (iso2 !== this.market()) return;
        const list: Automation[] = res.status ? (res.data?.automations ?? []) : [];
        // A fresh copy of what is saved, unless the admin is mid-edit.
        for (const a of list) {
          const id = `${iso2}:${a.key}`;
          this.autoDrafts[id] ??= { subject: a.subject, body: a.bodyHtml };
        }
        this.automations.set(list);
      });
  }

  autoDraft(a: Automation): { subject: string; body: string } {
    const id = `${this.market()}:${a.key}`;
    this.autoDrafts[id] ??= { subject: a.subject, body: a.bodyHtml };
    return this.autoDrafts[id];
  }

  autoReady(a: Automation): boolean {
    const d = this.autoDraft(a);
    return Boolean(d.subject.trim()) && this.hasText(d.body) && !this.busy();
  }

  /** Saves the email, and turns the automation on or off (enabled). */
  saveAutomation(a: Automation, enabled: boolean, toggle?: MatSlideToggle): void {
    const d = this.autoDraft(a);
    if (!d.subject.trim() || !this.hasText(d.body)) {
      this.snackBar.open("Write a subject and the email first", "Close", { duration: 4000 });
      // The toggle flipped itself on the click; [checked] has not changed, so put it back by hand.
      if (toggle) toggle.checked = a.enabled;
      this.loadAutomations();
      return;
    }
    if (
      enabled &&
      !a.enabled &&
      !window.confirm(
        `Turn on "${a.label}" in ${this.market()}?\n\n` +
          "From now on, each shopper who meets the rule is sent this email straight away - once only, ever."
      )
    ) {
      if (toggle) toggle.checked = a.enabled;
      return;
    }
    this.busy.set(`auto:${a.key}`);
    this.requestService
      .post<IApiResponse>("/api/v2/email-outreach/automations", {
        automation: a.key,
        iso2: this.market(),
        enabled,
        subject: d.subject,
        bodyHtml: d.body,
      })
      .subscribe({
        next: (res) => {
          this.busy.set("");
          this.snackBar.open(res.message || (res.status ? "Saved" : "Not saved"), "Close", {
            duration: 5000,
          });
          if (!res.status && toggle) toggle.checked = a.enabled;
          this.loadAutomations();
        },
        error: () => {
          this.busy.set("");
          this.snackBar.open("The automated email could not be saved", "Close", { duration: 4000 });
          if (toggle) toggle.checked = a.enabled;
          this.loadAutomations();
        },
      });
  }

  /** A test of an automated email, with example cart figures. */
  sendAutoTest(a: Automation, toTestInbox = false): void {
    if (!this.autoReady(a)) return;
    const d = this.autoDraft(a);
    this.busy.set(`${toTestInbox ? "inbox" : "test"}:auto:${a.key}`);
    this.requestService
      .post<IApiResponse>("/api/v2/email-outreach/test", {
        iso2: this.market(),
        subject: d.subject,
        bodyHtml: d.body,
        toTestInbox,
      })
      .subscribe({
        next: (res) => {
          this.busy.set("");
          this.snackBar.open(res.message || (res.status ? "Test sent" : "Test failed"), "Close", {
            duration: 5000,
          });
        },
        error: () => {
          this.busy.set("");
          this.snackBar.open("The test email could not be sent", "Close", { duration: 4000 });
        },
      });
  }

  /** Delivery as a share of the cart total, e.g. "52%". */
  ratio(fee: number | null, total: number | null): string {
    return fee != null && total ? `${Math.round((fee / total) * 100)}%` : "-";
  }

  /** `background`: the progress poll (silent, no session renewal, no /login redirect). */
  loadCampaigns(background = false): void {
    const iso2 = this.market();
    this.requestService
      .get<IApiResponse>(`/api/v2/email-outreach/campaigns?iso2=${iso2}`, { background })
      .subscribe((res) => {
        if (iso2 !== this.market()) return;
        this.campaigns.set(res.status ? (res.data?.campaigns ?? []) : []);
      });
  }

  labelOf(key: string): string {
    return this.segments().find((s) => s.key === key)?.label ?? key;
  }

  private hasText(html: string): boolean {
    return (
      html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim().length > 0
    );
  }

  ready(key: string): boolean {
    const d = this.draft(key);
    return Boolean(d.subject.trim()) && this.hasText(d.body) && !this.busy();
  }

  /** To the admin's own address, or (toTestInbox) to the team's test inbox. */
  sendTest(seg: Segment, toTestInbox = false): void {
    if (!this.ready(seg.key)) return;
    const d = this.draft(seg.key);
    this.busy.set(`${toTestInbox ? "inbox" : "test"}:${seg.key}`);
    this.requestService
      .post<IApiResponse>("/api/v2/email-outreach/test", {
        iso2: this.market(),
        subject: d.subject,
        bodyHtml: d.body,
        toTestInbox,
      })
      .subscribe({
        next: (res) => {
          this.busy.set("");
          this.snackBar.open(res.message || (res.status ? "Test sent" : "Test failed"), "Close", {
            duration: 5000,
          });
        },
        error: () => {
          this.busy.set("");
          this.snackBar.open("The test email could not be sent", "Close", { duration: 4000 });
        },
      });
  }

  send(seg: Segment): void {
    if (!this.ready(seg.key) || seg.eligible === 0) return;
    const d = this.draft(seg.key);
    const ok = window.confirm(
      `Send "${d.subject}" to ${seg.eligible} user(s) in "${seg.label}" (${this.market()})?\n\n` +
        "Each person gets their own email, sent in the background (up to 1,500 a day). " +
        (seg.skippedRecent
          ? `${seg.skippedRecent} user(s) emailed in the last 7 days are skipped. `
          : "") +
        "This cannot be undone once sending starts."
    );
    if (!ok) return;
    this.busy.set(`send:${seg.key}`);
    this.requestService
      .post<IApiResponse>("/api/v2/email-outreach/send", {
        segment: seg.key,
        iso2: this.market(),
        subject: d.subject,
        bodyHtml: d.body,
      })
      .subscribe({
        next: (res) => {
          this.busy.set("");
          this.snackBar.open(res.message || (res.status ? "Queued" : "Not sent"), "Close", {
            duration: 6000,
          });
          if (res.status) this.load();
        },
        error: () => {
          this.busy.set("");
          this.snackBar.open("The email could not be queued", "Close", { duration: 4000 });
        },
      });
  }

  /** "3 unsubscribed, 1 blocked" - why a campaign's emails were not sent. */
  notSentReasons(c: Campaign): string {
    const parts = [
      c.skippedUnsubscribed ? `${c.skippedUnsubscribed} unsubscribed` : "",
      c.skippedBlocked ? `${c.skippedBlocked} blocked` : "",
      c.skippedTestLimit ? `${c.skippedTestLimit} test limit` : "",
    ].filter(Boolean);
    return parts.join(", ");
  }
}
